import React, { FunctionComponent, useState, useMemo, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { round } from "lodash"
import { Dayjs } from "utils/dayjs"

import { Grid, GridItem } from "components/Grid"
import { Flex, FlexItem } from "components/Flex"
import Text from "components/Text/Text"

import Invoice, { InvoiceAutomaticAssignStatus, IPostInvoiceDto } from "models/Invoice"
import CustomError from "models/CustomError"
import { Guid, IValidationRule } from "@/types"
import ValidationService, { validationRules } from "services/ValidationService"
import Modal, { IModalProps } from "components/Modal/Modal"
import FormField from "components/FormField/FormField"
import Input from "components/Input/Input"
import Button from "components/Button/Button"
import InputDateTime from "components/InputDateTime/InputDateTime"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import toastMessages from "stores/toastMessages"
import { getDateStringFromMomentDate, toDateWithoutTimezone } from "utils/utils"
import { useTenantSettings } from "stores/tenantSettings"
import InputNumber from "components/InputNumber/InputNumber"
import Select, { ISelectOption, ISelectProps } from "components/Select/Select"
import useInputNumber from "components/InputNumber/hooks/useInputNumber"
import useFinanceSettings from "hooks/useFinanceSettings"
import Form from "components/Form/Form"
import useBookingPeriod from "hooks/useBookingPeriod"
import CostCategory, { CostCategoryType } from "models/CostCategory"
import useSuppliers from "hooks/useSuppliers"
import Supplier, { SupplierType } from "models/Supplier"
import Tooltip from "components/Tooltip/Tooltip"
import Status, { StatusType } from "components/Status/Status"
import alertModals from "stores/alertModals"
import InvoiceImportItem from "models/InvoiceImportItem"
import AssetService from "services/AssetService"
import Highlighter from "components/Highlighter/Highlighter"
import Reservation, { IGetReservationNumbersItemDto } from "models/Reservation"
import { Currency, GroupedControl } from "config/constants"

interface IInvoiceModalProps extends IModalProps {
    originalInvoice?: Invoice
    importData?: InvoiceImportItem
    onSubmit(invoice: IPostInvoiceDto, originalInvoice?: Invoice): Promise<{ id?: Guid, automaticAssignStatus?: InvoiceAutomaticAssignStatus, requestNumberValidationFailed?: boolean } | null>
}

enum InvoiceModalStatus {
    loading = "loading",
    idle = "idle",
}

const InvoiceModal: FunctionComponent<React.PropsWithChildren<IInvoiceModalProps>> = ({
    originalInvoice,
    importData,
    onClose,
    onSubmit
}) => {
    const { t } = useTranslation()
    const { tenantSettings } = useTenantSettings()
    const { costCategories } = useFinanceSettings()
    const currentBookingPeriod = useBookingPeriod()
    const suppliers = useSuppliers()
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [costCategory, setCostCategory] = useState<CostCategory | null>(null)
    const [requestNumber, setRequestNumber] = useState<string | null>(null)
    const [supplier, setSupplier] = useState<Supplier | null>(null)
    const [invoiceDate, setInvoiceDate] = useState<Dayjs>()
    const [invoiceNumber, setInvoiceNumber] = useState("")
    const [invoiceAmount, invoiceAmountNumber, setInvoiceAmount, setInitialInvoiceAmountNumber] = useInputNumber("")
    const [referenceNumber, setReferenceNumber] = useState("")
    const [referenceText, setReferenceText] = useState("")
    const [discountPercentage, discountPercentageNumber, setDiscountPercentage, setInitialDiscountPercentage] = useInputNumber(0)
    const bookingPeriod = originalInvoice?.bookingPeriod || currentBookingPeriod
    const isAutomaticAssignmentMessageShowed = !!costCategory?.hasAutomaticAssign
    const shouldUseRequestNumber = costCategory?.costCategoryType === CostCategoryType.sdc
    const [requestNumberValidationFailed, setRequestNumberValidationFailed] = useState(false)
    const [isForeignCurrencyAmountEnabled, setForeignCurrencyAmountEnabled] = useState(false)
    const [invoiceForeignCurrencyAmount, invoiceForeignCurrencyAmountNumber, setInvoiceForeignCurrencyAmount, setInitialInvoiceForeignCurrencyAmount] = useInputNumber("")
    const [invoiceForeignCurrency, setInvoiceForeignCurrency] = useState<Currency>()

    const costCategoriesOptions = useMemo<ISelectProps["options"]>(() => {
        return costCategories?.list.map(cc => ({
            label: cc.costCategoryLedgerAccount ? `${cc.costCategoryLedgerAccount} (${cc.costCategoryName})` : cc.costCategoryName,
            value: cc,
        }))
    }, [costCategories])

    const supplierOptions = useMemo<ISelectProps["options"]>(() => {
        return suppliers.map(s => ({
            label: `${s.number} (${s.name})`,
            value: s,
            group: s.type
        }))
    }, [suppliers])


    const getSupplierSelectGroupHeader = (group: SupplierType) => {
        switch (group) {
            case SupplierType.affiliatedCompany:
                return t("invoices.supplier.type.affiliatedCompany")
            case SupplierType.thirdParty:
                return t("invoices.supplier.type.thirdParty")
            default: return ""
        }
    }

    const isDateInClosedPeriod = useMemo(() => !!(currentBookingPeriod && invoiceDate && invoiceDate < currentBookingPeriod), [currentBookingPeriod, invoiceDate])

    useEffect(() => {
        if (!originalInvoice) return
        setRequestNumber(originalInvoice.requestNumber ?? null)
        setCostCategory((costCategories && costCategories.list.find(cc => cc.costCategoryId === originalInvoice?.costCategoryId)) ?? null)
        setSupplier(suppliers.find(sup => sup.id === originalInvoice?.supplierId) ?? null)
        setInvoiceDate(originalInvoice.invoiceDate)
        setInvoiceNumber(originalInvoice.invoiceNumber)
        setInitialInvoiceAmountNumber(originalInvoice.invoiceAmount.amount)
        setInitialInvoiceForeignCurrencyAmount(originalInvoice.invoiceForeignCurrencyAmount?.amount ?? "")
        setInvoiceForeignCurrency(originalInvoice.invoiceForeignCurrencyAmount?.currency)
        setReferenceNumber(originalInvoice.referenceNumber)
        setReferenceText(originalInvoice.referenceText ?? "")
        setRequestNumber(originalInvoice.requestNumber ?? null)
        setInitialDiscountPercentage(originalInvoice.discountPercentage)
        if (originalInvoice.invoiceForeignCurrencyAmount) {
            setForeignCurrencyAmountEnabled(true)
        }

    }, [originalInvoice, setInvoiceAmount, costCategories, suppliers, setInitialInvoiceAmountNumber, setInitialDiscountPercentage, setInitialInvoiceForeignCurrencyAmount])

    useEffect(() => {
        if (!importData) return
        setSupplier(suppliers.find(sup => sup.number === importData?.supplierNumber) ?? null)
        setInvoiceDate(importData.invoiceDate)
        setInvoiceNumber(importData.invoiceNumber)
        setInitialInvoiceAmountNumber(importData?.amount?.amount ?? "")
        setInitialInvoiceForeignCurrencyAmount(importData.foreignCurrencyAmount?.amount ?? "")
        setInvoiceForeignCurrency(importData.foreignCurrencyAmount?.currency)
        setReferenceNumber(importData.referenceNumber ?? "")
        setReferenceText(importData.referenceText ?? "")
        setInitialDiscountPercentage(0)
        setRequestNumber(importData.requestNumber ?? null)
        setCostCategory((costCategories && costCategories.list.find(cCategory => cCategory.costCategoryLedgerAccount === importData?.ledgerAccount)) ?? null)
        if (importData.foreignCurrencyAmount) {
            setForeignCurrencyAmountEnabled(true)
        }

    }, [importData, setInvoiceAmount, costCategories, suppliers, setInitialInvoiceAmountNumber, setInitialDiscountPercentage, setInitialInvoiceForeignCurrencyAmount])

    useEffect(() => {
        if (currentBookingPeriod === null && !!onClose) {
            toastMessages.showSystemError()
            onClose()
        }
    }, [currentBookingPeriod, onClose])

    const status = useMemo<InvoiceModalStatus>(() => {
        if (!currentBookingPeriod) {
            return InvoiceModalStatus.loading
        }

        return InvoiceModalStatus.idle
    }, [currentBookingPeriod])

    const validationErrors = useMemo((): { [x: string]: CustomError } => {
        let validationFields: [string, IValidationRule, any][] = [
            ["costCategory", validationRules.invoice.costCategory, costCategory],
            ["invoiceDate", validationRules.invoice.invoiceDate, invoiceDate],
            ["invoiceNumber", validationRules.invoice.invoiceNumber, invoiceNumber],
            ["invoiceAmount", validationRules.invoice.invoiceAmount, invoiceAmount],
            ["referenceNumber", validationRules.invoice.referenceNumber, referenceNumber],
            ["discountPercentage", validationRules.invoice.discountPercentage, discountPercentage],
        ]
        const foreignCurrencyValidation = !!(isForeignCurrencyAmountEnabled && (invoiceForeignCurrencyAmount || invoiceForeignCurrency)) && [
            ["invoiceForeignCurrencyAmount", validationRules.invoice.invoiceForeignCurrencyAmount, invoiceForeignCurrencyAmount],
            ["invoiceForeignCurrency", validationRules.invoice.invoiceForeignCurrency, invoiceForeignCurrency],
        ] as [string, IValidationRule, any][]
        return ValidationService.validateMultiple([...validationFields, ...(foreignCurrencyValidation || [])])
    }, [costCategory,
        invoiceDate,
        invoiceNumber,
        invoiceAmount,
        referenceNumber,
        discountPercentage,
        isForeignCurrencyAmountEnabled,
        invoiceForeignCurrencyAmount,
        invoiceForeignCurrency])

    const discount = useMemo<number>(() => {
        if (!invoiceAmountNumber || !discountPercentageNumber) return 0
        const result = invoiceAmountNumber * discountPercentageNumber / 100
        return round(result, 2)
    }, [discountPercentageNumber, invoiceAmountNumber])

    const discountForeignCurrency = useMemo<number>(() => {
        if (!invoiceForeignCurrencyAmountNumber || !discountPercentageNumber) return 0
        const result = invoiceForeignCurrencyAmountNumber * discountPercentageNumber / 100
        return round(result, 2)
    }, [discountPercentageNumber, invoiceForeignCurrencyAmountNumber])

    const netInvoiceAmount = useMemo<number>(() => {
        if (invoiceAmountNumber === null) return 0
        if (!discount || ValidationService.isFieldInvalid(validationErrors, 'discountPercentage')) return invoiceAmountNumber
        return invoiceAmountNumber - discount
    }, [discount, invoiceAmountNumber, validationErrors])

    const netInvoiceForeignCurrencyAmount = useMemo<number>(() => {
        if (invoiceForeignCurrencyAmountNumber === null) return 0
        if (!discountForeignCurrency || ValidationService.isFieldInvalid(validationErrors, 'discountPercentage')) return invoiceForeignCurrencyAmountNumber
        return invoiceForeignCurrencyAmountNumber - discountForeignCurrency
    }, [discountForeignCurrency, invoiceForeignCurrencyAmountNumber, validationErrors])

    const amountForeignCurrencyLessDiscount = (invoiceForeignCurrencyAmount && invoiceForeignCurrency && isForeignCurrencyAmountEnabled) ? `(${t('common.amountWithCurrency', { count: netInvoiceForeignCurrencyAmount, currency: invoiceForeignCurrency })})` : ''

    async function handleSubmit(event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault()
        event.stopPropagation()

        setIsSubmitted(true)

        if (Object.keys(validationErrors).length > 0
            || !costCategory
            || !invoiceDate
            || !invoiceNumber
            || !referenceNumber
            || invoiceAmountNumber === null
            || discountPercentageNumber === null
            || !tenantSettings?.defaultCurrency
            || !bookingPeriod) {
            return
        }

        setIsSubmitting(true)

        const res = await onSubmit({
            costCategoryId: costCategory.costCategoryId!,
            invoiceDate: getDateStringFromMomentDate(invoiceDate),
            invoiceNumber,
            invoiceAmount: {
                amount: invoiceAmountNumber,
                currency: tenantSettings.defaultCurrency
            },
            invoiceForeignCurrencyAmount: (
                isForeignCurrencyAmountEnabled
                && invoiceForeignCurrencyAmountNumber
                && invoiceForeignCurrency) ? {
                amount: invoiceForeignCurrencyAmountNumber,
                currency: invoiceForeignCurrency
            } : undefined,
            discountPercentage: discountPercentageNumber,
            referenceNumber,
            referenceText,
            requestNumber: requestNumber ?? undefined,
            bookingPeriod: toDateWithoutTimezone(bookingPeriod),
            supplierId: supplier?.id || null,
            invoiceImportItemId: importData?.id,
        }, originalInvoice)

        setIsSubmitting(false)

        if (res) {
            if (res.requestNumberValidationFailed) {
                setRequestNumberValidationFailed(true)
                return
            }
            switch (res.automaticAssignStatus) {
                case (InvoiceAutomaticAssignStatus.noReferenceValues): {
                    alertModals.show({
                        title: t('common.attention'),
                        children: t("invoices.automaticAssignment.noReferenceValuesMessage"),
                        buttonLabel: t('common.ok'),
                        maxWidth: "586px",
                        onClose: () => onClose && onClose()

                    })
                    return
                }
                case (InvoiceAutomaticAssignStatus.noRelevantAssets): {
                    alertModals.show({
                        title: t('common.attention'),
                        children: t("invoices.automaticAssignment.noRelevantAssetsMessage"),
                        buttonLabel: t('common.ok'),
                        maxWidth: "586px",
                        onClose: () => onClose && onClose()

                    })
                    return
                }
                default: {
                    onClose && onClose()
                }
            }
        } else {
            toastMessages.show({ title: t("message.systemError"), type: "error" })
        }
    }

    function costCategoryOptionTemplate(option: ISelectOption<CostCategory>) {
        return <Flex justifyContent='space-between'>
            <FlexItem className='ellipsis max-w-40'>
                {option.value.costCategoryLedgerAccount || t("common.noValue")}
            </FlexItem>
            <FlexItem className="ellipsis color-grey-500 pl-1 max-w-60">
                {option.value.costCategoryName}
            </FlexItem>
        </Flex>
    }

    function supplierOptionTemplate(option: ISelectOption<Supplier>) {
        return <Tooltip content={option.value.name} trigger='hover'>
            <Grid columns='minmax(0,8rem) minmax(0, auto)'>
                <Text>
                    {option.value.number}
                </Text>
                <Text className="color-grey-500 white-space-nowrap overflow-hidden text-overflow-ellipsis ">
                    {option.value.name}
                </Text>
            </Grid>
        </Tooltip>
    }
    return (
        <Modal
            title={t(originalInvoice ? 'invoices.editInvoiceTitle' : 'invoices.addInvoiceTitle')}
            maxWidth="676px"
            onClose={onClose}
            isBusy={status === InvoiceModalStatus.loading}
            footer={
                status !== InvoiceModalStatus.loading &&
                <Flex justifyContent="flex-end">
                    <Button
                        theme="secondary"
                        label={t('common.cancel')}
                        onClick={onClose}
                        disabled={isSubmitting} />
                    <Button
                        label={t(originalInvoice ? 'common.save' : 'invoices.addInvoice')}
                        onClick={handleSubmit}
                        className="ml-2"
                        disabled={isSubmitting}
                        loading={isSubmitting} />
                </Flex>
            }>
            <Form onSubmit={handleSubmit}>
                {isAutomaticAssignmentMessageShowed && <Status
                    type={StatusType.caution}>
                    {t('invoices.automaticAssignment.cautionMessage')}
                </Status>}
                {isDateInClosedPeriod && <Status
                    type={StatusType.caution}>
                    {t('invoices.closedBookingPeriodTooltip')}
                </Status>}
                <Grid columns="repeat(6, 1fr)"
                    gap="2.4rem">

                    <GridItem area="auto / 1 / auto / 4">
                        <FormField
                            label={t('invoices.costCategory')}>
                            <Select
                                value={costCategory}
                                options={costCategoriesOptions}
                                onChange={option => setCostCategory(option?.value || null)}
                                isInvalid={isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'costCategory')}
                                optionTemplate={costCategoryOptionTemplate}
                            />
                        </FormField>
                        {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'costCategory') &&
                            <ValidationMessage>{validationErrors.costCategory.message}</ValidationMessage>
                        }
                    </GridItem>
                    <GridItem area="auto  / 4 / auto / 7">
                        <FormField
                            label={t('invoices.supplier.label')}
                            optional
                        >

                            <Select
                                value={supplier}
                                options={supplierOptions}
                                isUseOptimization
                                groupOptions={options => {
                                    return [
                                        {
                                            label: getSupplierSelectGroupHeader(SupplierType.affiliatedCompany),
                                            options: options.filter(option => option.group === SupplierType.affiliatedCompany),
                                        },
                                        {
                                            label: getSupplierSelectGroupHeader(SupplierType.thirdParty),
                                            options: options.filter(option => option.group === SupplierType.thirdParty),
                                        }, {
                                            label: getSupplierSelectGroupHeader(SupplierType.unknown),
                                            options: options.filter(option => option.group === SupplierType.unknown),
                                        }
                                    ]
                                }

                                }
                                optionTemplate={supplierOptionTemplate}
                                onChange={option => setSupplier(option?.value || null)} />
                        </FormField>
                    </GridItem>
                    {shouldUseRequestNumber && <>
                        <GridItem area="auto / 1 / auto / 4">
                            <FormField
                                label={t('invoices.requestNo')}>
                                <Select
                                    value={requestNumber}
                                    getOptions={search => AssetService.getReservationNumbers({ search })}
                                    optionTemplate={(option: ISelectOption<string>, highlight: string) => (
                                        <Highlighter
                                            text={option.value}
                                            highlight={highlight ? [Reservation.formatReservationNumber(highlight) || "", highlight] : ""}
                                        />
                                    )}
                                    formatOptionValue={(value: IGetReservationNumbersItemDto) => value.number}
                                    onChange={option => {
                                        setRequestNumber(option?.value || null);
                                        setRequestNumberValidationFailed(false);
                                    }}
                                    isInvalid={isSubmitted && requestNumberValidationFailed}

                                />
                            </FormField>
                            {isSubmitted && requestNumberValidationFailed &&
                                <ValidationMessage>{t("invoices.requestHasBeenDeletedError")}</ValidationMessage>
                            }
                        </GridItem>
                        <GridItem area="auto / 4 / auto / 7" />
                    </>
                    }

                    <GridItem area="auto / 1 / auto / 3">
                        <FormField
                            label={t('invoices.bookingPeriod')}
                            isText>
                            {bookingPeriod?.format(t('config.dateMonthYearFormat'))}
                        </FormField>
                    </GridItem>

                    <GridItem area="auto / 3 / auto / 5">
                        <FormField
                            label={t('invoices.invoiceDate')}>
                            <InputDateTime
                                type="date"
                                date={invoiceDate}
                                onDateChange={newDate => {
                                    setInvoiceDate(newDate || undefined)
                                }}
                                isInvalid={isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'invoiceDate')}
                                noSaveButton
                            />
                        </FormField>
                        {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'invoiceDate') &&
                            <ValidationMessage>{validationErrors.invoiceDate.message}</ValidationMessage>
                        }
                    </GridItem>

                    <GridItem area="auto / 5 / auto / 7">
                        <FormField
                            label={t('invoices.invoiceNumber')}>
                            <Input
                                value={invoiceNumber}
                                isInvalid={isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'invoiceNumber')}
                                onChange={e => setInvoiceNumber(e.target.value)} />
                        </FormField>
                        {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'invoiceNumber') &&
                            <ValidationMessage>{validationErrors.invoiceNumber.message}</ValidationMessage>
                        }
                    </GridItem>

                    <GridItem area="auto / 1 / auto / 5">
                        <FormField
                            label={t('invoices.invoiceAmount')}
                            note={
                                <>
                                    {isForeignCurrencyAmountEnabled && <>{`(${t('common.optional')})`} <br /><br /></>}
                                    {`${t('invoices.invoiceAmountLessDiscount')}: ${t('common.amountWithCurrency', { count: netInvoiceAmount, currency: tenantSettings?.defaultCurrency })} `}
                                    {amountForeignCurrencyLessDiscount}
                                </>}
                            helpTooltip={{
                                content: <Text className="white-space-pre-line">{t('invoices.invoiceAmountTooltip')}</Text>
                            }}
                            after={<Button
                                label={isForeignCurrencyAmountEnabled ? t("invoices.removeForeignCurrency") : t("invoices.enterInForeignCurrency")}
                                theme='link'
                                onClick={() => setForeignCurrencyAmountEnabled(prev => !prev)} />}
                        >
                            <GridItem>
                                <InputNumber
                                    value={invoiceAmount}
                                    minDecimals={2}
                                    maxDecimals={2}
                                    isSubmitted={isSubmitted}
                                    isInvalid={ValidationService.isFieldInvalid(validationErrors, 'invoiceAmount')}
                                    onChange={setInvoiceAmount}
                                    after={<div className="px-2">{tenantSettings?.defaultCurrency}</div>}
                                />
                                {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'invoiceAmount') &&
                                    <ValidationMessage>{t("invoices.amountInLocalCurrencyNotValid")}</ValidationMessage>
                                }
                            </GridItem>

                            {isForeignCurrencyAmountEnabled &&
                                <Grid columns="auto minMax(123px, 29%)" className="mt-3">
                                    <Flex flow="column">
                                        <InputNumber
                                            value={invoiceForeignCurrencyAmount}
                                            minDecimals={2}
                                            maxDecimals={2}
                                            isSubmitted={isSubmitted}
                                            isInvalid={ValidationService.isFieldInvalid(validationErrors, 'invoiceForeignCurrencyAmount')}
                                            onChange={setInvoiceForeignCurrencyAmount}
                                            grouped={GroupedControl.left}
                                        />
                                    </Flex>
                                    <Flex flow="column" >
                                        <Select
                                            value={invoiceForeignCurrency}
                                            options={Object.values(Currency).filter(currency => currency !== tenantSettings?.defaultCurrency).map(currency => ({ label: currency, value: currency }))}
                                            onChange={(option) => setInvoiceForeignCurrency(option?.value)}
                                            grouped={GroupedControl.right}
                                            isInvalid={isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'invoiceForeignCurrency')}
                                            isInModal
                                        />
                                    </Flex>
                                    <GridItem>
                                        {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'invoiceForeignCurrencyAmount') &&
                                            <ValidationMessage>{t("invoices.amountInForeignCurrencyNotValid")}</ValidationMessage>
                                        }
                                        {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'invoiceForeignCurrency') &&
                                            <ValidationMessage>{t("invoices.selectCurrency")}</ValidationMessage>
                                        }
                                    </GridItem>
                                </Grid>}
                        </FormField>
                    </GridItem>

                    <GridItem area="auto / 5 / auto / 7">
                        <FormField
                            label={t('invoices.discount')}
                            note={<>{t('common.amountWithCurrency', { count: discount, currency: tenantSettings?.defaultCurrency })}
                            {isForeignCurrencyAmountEnabled
                            && invoiceForeignCurrencyAmount
                            && invoiceForeignCurrency &&
                             ` (${t('common.amountWithCurrency', { count: discountForeignCurrency, currency: invoiceForeignCurrency })})`}
                            </>}>
                            <InputNumber
                                value={discountPercentage}
                                minDecimals={2}
                                maxDecimals={2}
                                isSubmitted={isSubmitted}
                                isInvalid={ValidationService.isFieldInvalid(validationErrors, 'discountPercentage')}
                                onChange={setDiscountPercentage}
                                after={<div className="px-2">%</div>}
                            />
                        </FormField>
                        {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'discountPercentage') &&
                            <ValidationMessage>{validationErrors.discountPercentage.message}</ValidationMessage>
                        }
                    </GridItem>

                    <GridItem area="auto / 1 / auto / 4">
                        <FormField
                            label={t('invoices.referenceNumber')}
                            helpTooltip={{
                                content: <Text className="white-space-pre-line">{t('invoices.referenceNumberTooltip')}</Text>
                            }}>
                            <Input
                                value={referenceNumber}
                                isInvalid={isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'referenceNumber')}
                                onChange={e => setReferenceNumber(e.target.value)} />
                        </FormField>
                        {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'referenceNumber') &&
                            <ValidationMessage>{validationErrors.referenceNumber.message}</ValidationMessage>
                        }
                    </GridItem>
                    <GridItem area="auto / 4 / auto / 7">
                        <FormField
                            label={t('invoices.referenceText')}
                            helpTooltip={{
                                content: <Text className="white-space-pre-line">{t('invoices.referenceTextTooltip')}</Text>
                            }}
                            optional>
                            <Input
                                value={referenceText}
                                isInvalid={isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'referenceText')}
                                onChange={e => setReferenceText(e.target.value)} />
                        </FormField>
                        {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'referenceText') &&
                            <ValidationMessage>{validationErrors.referenceText.message}</ValidationMessage>
                        }
                    </GridItem>
                </Grid>
            </Form >
        </Modal >
    )
}

export default InvoiceModal
